/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from "react";
import { useTradeOrganizationContext } from "../../context/TradeOrganizationContext";
import {
  TextArea,
  InputField,
  MyMessage,
  AddPageShell,
  AddSection,
} from "../components/Component";
import { defaultImage } from "../../lib/libraries/imageKit/ImageKit";
import { ImageKitUpload } from "../../ui/ImageKitLibrary";
import { FOLDERS } from "../doseeData/utils/Utils";
import { AiFillBuild } from "react-icons/ai";
import {
  FiFlag,
  FiTarget,
  FiImage,
  FiAlignLeft,
} from "react-icons/fi";

const Add = () => {
  const { createOneTradeOrganization } = useTradeOrganizationContext();

  const [error, setError] = useState(false);
  const [message, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const [name, setName] = useState(null);
  const [acronym, setAcronym] = useState(null);
  const [headquarters, setHeadquarters] = useState(null);
  const [objectives, setObjectives] = useState(null);
  const [debuted_on, setDebutedOn] = useState(null);
  const [mission, setMission] = useState(null);
  const [vision, setVision] = useState(null);

  const [thumbnail, setThumbnail] = useState(defaultImage);
  const [thumbnailId, setThumbnailId] = useState("img_cdn_id");
  const [website, setWebsite] = useState(null);

  const [description, setDescription] = useState(null);

  const tradeOrganizationRoutes = "/trade/org";

  const cleanFields = () => {
    setLoading(false);
    setSuccess(true);
    setName(null);
    setAcronym(null);
    setHeadquarters(null);
    setObjectives(null);
    setDebutedOn(null);
    setMission(null);
    setVision(null);
    setDescription(null);
    setThumbnail(defaultImage);
    setThumbnailId("image_cdn_id");
    setWebsite(null);
  };

  const responses = (isError: any, msg: any) => {
    setError(isError);
    setSuccess(!isError);
    setLoading(false);
    setErrorMessage(msg);
  };

  const addOneTradeOrganizationHandler = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    if (!thumbnail || !thumbnailId) {
      setLoading(false);
      return responses(true, "Image Required");
    } else {
      await sendDBRequest();
    }
  };

  const sendDBRequest = async () => {
    const data = {
      name,
      acronym,
      headquarters,
      chief_officer: "...",
      objectives,
      debuted_on,
      mission,
      vision,
      description,
      thumbnail,
      image_cdn_id: thumbnailId,
      website,
    };

    const { status } = await createOneTradeOrganization(data);
    if (status === 200) {
      cleanFields();
      responses(false, message);
    } else {
      setLoading(false);
      responses(true, message);
    }
  };

  return (
    <AddPageShell
      backRoute={tradeOrganizationRoutes}
      eyebrow="Geography • Trade Organisations"
      title="Create Trade Organisation"
      subtitle="Add a recognised body with identity, purpose and an official logo."
      icon={<AiFillBuild className="h-5 w-5" />}
      loading={loading}
      submitLabel="Add Trade Organisation"
      onSubmit={addOneTradeOrganizationHandler}
      onSubmitClick={() => {
        setError(false);
        setSuccess(false);
        setErrorMessage(null);
      }}
      message={
        <MyMessage
          error={error}
          message={message}
          success={success}
          onExpired={() => {
            setError(false);
            setSuccess(false);
          }}
          onClick={() => {
            setError(false);
            setSuccess(false);
            setErrorMessage("");
          }}
        />
      }
    >
      <AddSection
        title="Organisation Logo"
        hint="PNG or JPG. Use a clear, official logo where possible."
        icon={<FiImage className="h-4 w-4" />}
      >
        <ImageKitUpload
          thumbnail={thumbnail}
          thumbnailId={thumbnailId}
          setThumbnail={setThumbnail}
          setThumbnailId={setThumbnailId}
          folder={FOLDERS.TRADE_ORGANIZATIONS}
        />
      </AddSection>

      <AddSection
        title="Identity"
        hint="Official name, acronym, website and headquarters."
        icon={<FiFlag className="h-4 w-4" />}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <InputField
            props={{
              id: "title",
              title: "Name",
              type: "text",
              required: true,
              value: name ? name : "",
              onChange: (e: any) => {
                setName(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
          <InputField
            props={{
              id: "acronym",
              title: "Acronym",
              type: "text",
              value: acronym ? acronym : "",
              onChange: (e: any) => {
                setAcronym(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
          <InputField
            props={{
              id: "website",
              title: "Website",
              type: "text",
              value: website ? website : "",
              onChange: (e: any) => {
                setWebsite(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
          <InputField
            props={{
              id: "headquarters",
              title: "Headquarters",
              type: "text",
              value: headquarters ? headquarters : "",
              onChange: (e: any) => {
                setHeadquarters(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
          <InputField
            props={{
              id: "debuted_on",
              title: "Debuted On",
              type: "text",
              value: debuted_on ? debuted_on : "",
              onChange: (e: any) => {
                setDebutedOn(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
        </div>
      </AddSection>

      <AddSection
        title="Purpose & Narrative"
        hint="Objectives, mission and vision."
        icon={<FiTarget className="h-4 w-4" />}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <TextArea
            props={{
              id: "objectives",
              title: "Objectives",
              type: "text",
              value: objectives ? objectives : "",
              onChange: (e: any) => {
                setObjectives(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
          <TextArea
            props={{
              id: "mission",
              title: "Mission",
              type: "text",
              value: mission ? mission : "",
              onChange: (e: any) => {
                setMission(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
          <TextArea
            props={{
              id: "vision",
              title: "Vision",
              type: "text",
              value: vision ? vision : "",
              onChange: (e: any) => {
                setVision(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
        </div>
      </AddSection>

      <AddSection
        title="Description"
        hint="A concise summary used across listings."
        icon={<FiAlignLeft className="h-4 w-4" />}
      >
        <TextArea
          props={{
            id: "description",
            title: "Description",
            type: "text",
            required: true,
            value: description ? description : "",
            onChange: (e: any) => {
              setDescription(e.target.value);
              setError(null);
              setSuccess(null);
            },
          }}
        />
      </AddSection>
    </AddPageShell>
  );
};

export default Add;
